import React, { useEffect, useState, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button, Flex, notification, Alert } from "antd";
import axios from "axios";
import MeetingAPI from '../../restAPI/MeetingAPI';
import AnnotationAPI from '../../restAPI/AnnotationAPI';
import { isAnnotationUrl, isAnnotationAvailable } from '../../utils/annotationUtils';
import AnnotationIframe from '../../components/AnnotationIframe';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
).toString();

export default function PDFSignature({ signatureList, agendaID, itemId, attachmentId, fileName, userDetailId, token, companyId, onClose, closeOnSuccess,callbackfunpdf }) {
    const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };
    const [numPages, setNumPages] = useState(null);
    const [signatureImage, setSignatureImage] = useState(null); // Signature image
    const [isSelecting, setIsSelecting] = useState(false); // Flag to enable area selection
    const [signEnable, setSignEnable] = useState(false); // Flag to enable area selection
    const [selectionArea, setSelectionArea] = useState(null); // Selected area for the signature
    const [activePage, setActivePage] = useState(null); // Current page for selection
    const [previousSignatureList, setPreviousSignatureList] = useState([]);
    const pageRefs = useRef([]); // Refs for individual pages
    const [pdfFile, setPdfFile] = useState(null);

    useEffect(() => {
        if (userDetailId && fileName) {
            // Check if this is an annotation URL
            if (isAnnotationUrl(fileName)) {
                setPdfFile(fileName);
            } else {
                // Fallback to legacy URL format
                setPdfFile(
                    `${process.env.REACT_APP_REDIRECT_URL}/PDFSignature/userDetailsId/${userDetailId}/connection-id/${fileName}`
                );
            }
        }
    }, [userDetailId, fileName, process.env.REACT_APP_REDIRECT_URL]);


    const getUserSignature = async (userDetailId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_REDIRECT_URL}/signature/userDetailsId/${userDetailId}`);
            if (response.status === 200 && response.data.data) {
                setSignatureImage(response.data.data);
            } else {
                openNotificationWithIcon("error", "Retrieve", "Failed to fetch user signature", "bottomLeft");
            }
        } catch (error) {
            openNotificationWithIcon("error", "Retrieve", `Error fetching user signature: ${error}`, "bottomLeft");
        }
    };

    useEffect(() => {
        getUserSignature(userDetailId);
    }, [userDetailId]);

    const handleDocumentLoad = ({ numPages }) => {
        setNumPages(numPages);
        pageRefs.current = new Array(numPages).fill(null).map(() => React.createRef());
    };

    const startSelection = (e, pageNumber) => {
        if (!isSelecting) return;

        const rect = pageRefs.current[pageNumber - 1].current.getBoundingClientRect();
        const startX = e.clientX - rect.left;
        const startY = e.clientY - rect.top;

        setActivePage(pageNumber);
        setSelectionArea({ startX, startY, endX: startX, endY: startY });
    };

    const updateSelection = (e) => {
        if (!isSelecting || !selectionArea) return;

        const rect = pageRefs.current[activePage - 1].current.getBoundingClientRect();
        const endX = e.clientX - rect.left;
        const endY = e.clientY - rect.top;

        setSelectionArea((prev) => ({
            ...prev,
            endX,
            endY,
        }));
    };

    const endSelection = () => {
        setIsSelecting(false);
    };

    const handleConfirm = async () => {
        if (!selectionArea || !signatureImage) {
            notification.warning({
                message: "Warning",
                description: "Please select an area before confirming.",
            });
            return;
        }
        try {

            const timestamp = new Date().toISOString(); // Get the current date and time in ISO format

            const formdata = {
                data: {
                    ...selectionArea,
                    image: signatureImage,
                    timestamp: timestamp, // Include timestamp in the signature data
                },
                itemId: itemId,
                attachmentId: attachmentId,
            }
            const res = await MeetingAPI.addSignature(formdata, agendaID, token, companyId);
            if (res.status === 200) {
                // notification.success({
                //     message: "Success",
                //     description: "Signature placed in the selected area.",
                //     placement:"bottomLeft",
                    
                // });
                // openNotificationWithIcon("success", "Successfull", "Signature placed in the selected area.", "bottomLeft");
                callbackfunpdf("success", "Successfull", "Signature placed in the selected area.", "bottomLeft")
                closeOnSuccess();
            } else {
                notification.warning({
                    message: "Warning",
                    description: "Network Issue",
                });
            }

        } catch (error) {
            notification.warning({
                message: "Warning",
                description: `Server Issue ${error}`,
            });
        }
    };

    useEffect(() => {
        const filteredSignatures = signatureList.filter((item) => item.signature).map((item) => item.signature);
        setPreviousSignatureList(filteredSignatures);
    }, [signatureList])

    return (
        <>
            {contextHolder}
            
            {/* Check if this is an annotation URL and annotation system is enabled */}
            {isAnnotationAvailable(fileName) ? (
                <div style={{ height: 'calc(100svh - 260px)' }}>
                    <Alert
                        message="Annotation System Active"
                        description="This document is being viewed through the annotation system. Signature functionality may be limited."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <AnnotationIframe 
                        annotationUrl={fileName}
                        style={{ height: 'calc(100% - 60px)' }}
                    />
                </div>
            ) : (
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        position: "relative",
                        userSelect: isSelecting ? "none" : "auto",
                        overflowX: 'auto',
                        height: 'calc(100svh - 260px)'
                    }}
                    onMouseMove={updateSelection}
                    onMouseUp={endSelection}
                >
                    <Document file={pdfFile} onLoadSuccess={handleDocumentLoad}>
                        {Array.from(new Array(numPages), (el, index) => (
                            <div
                                key={`page-container-${index + 1}`}
                                style={{ position: "relative" }}
                                ref={pageRefs.current[index]}
                                onMouseDown={(e) => startSelection(e, index + 1)}
                            >
                                <Page key={`page-${index + 1}`} pageNumber={index + 1} width={800} />

                                {/* Render Selection Area on Current Page */}
                                {selectionArea && activePage === index + 1 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: `${selectionArea.startX}px`,
                                            top: `${selectionArea.startY}px`,
                                            width: `${selectionArea.endX - selectionArea.startX}px`,
                                            height: `${selectionArea.endY - selectionArea.startY}px`,
                                            border: "2px dashed red",
                                            zIndex: 1000,
                                        }}
                                    />
                                )}

                                {/* Render Signature in Selected Area */}
                                {selectionArea && !isSelecting && activePage === index + 1 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: `${selectionArea.startX}px`,
                                            top: `${selectionArea.startY}px`,
                                            textAlign: "center",
                                            zIndex: 1000,
                                        }}
                                    >
                                        <img
                                            src={signatureImage}
                                            alt="Signature"
                                            style={{
                                                width: `${selectionArea.endX - selectionArea.startX}px`,
                                                height: `${selectionArea.endY - selectionArea.startY}px`,
                                            }}
                                        />
                                        <div
                                            style={{
                                                marginTop: "5px",
                                                fontSize: "12px",
                                                color: "#555",
                                            }}
                                        >
                                            {new Date().toLocaleString()}
                                        </div>
                                    </div>
                                )}

                                {/* Render Previous Signatures on This Page */}
                                {previousSignatureList.map((signature, idx) => (
                                    <div
                                        key={`prev-signature-container-${idx}`}
                                        style={{
                                            position: "absolute",
                                            left: `${signature.startX}px`,
                                            top: `${signature.startY}px`,
                                            zIndex: 900,
                                        }}
                                    >
                                        {/* Signature Image */}
                                        <img
                                            src={signature.image}
                                            alt={`Previous Signature ${idx}`}
                                            style={{
                                                width: `${signature.endX - signature.startX}px`,
                                                height: `${signature.endY - signature.startY}px`,
                                            }}
                                        />
                                        {/* Timestamp */}
                                        <div
                                            style={{
                                                marginTop: "5px", // Adds space between signature and timestamp
                                                fontSize: "12px", // Small font for timestamp
                                                color: "#555", // Gray color for visibility
                                                textAlign: "center",
                                                width: "100%",
                                            }}
                                        >
                                            {new Date(signature.timestamp).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </Document>
                </div>
            )}

            <Flex align="center" justify="flex-end" gap={24} style={{marginTop:24}}>
                <Button
                    type="default"
                    onClick={() => {
                        setSelectionArea(null);
                        onClose();
                    }}
                >
                    Cancel
                </Button>
                {!isAnnotationAvailable(fileName) && (
                    <>
                        <Button
                            type="primary"
                            onClick={() => {
                                if (signEnable) {
                                    setSignEnable(false);
                                    setSelectionArea(null)
                                } else {
                                    setSignEnable(true)
                                    setIsSelecting(true)
                                }
                            }}
                        >
                            {signEnable ? 'Clear' : 'Sign'}
                        </Button>
                        <Button type="primary" onClick={handleConfirm}>
                            Confirm
                        </Button>
                    </>
                )}
            </Flex>
        </>
    );
}
